import * as React from 'react';
import { LayoutLogin } from '@/components/layout';
import { AccountDetails } from '@/components/dashboard';
import { useSession } from 'next-auth/react';
import { UpdateUser } from '@/interfaces';
import { GET_USER_ME, userService, verificationsService } from '@/api';
import { useNotification } from '@/hooks';
import { FieldValues } from 'react-hook-form';
import { formatErrorMessage, formatPhoneRemove } from '@/lib';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';
import { useQuery } from '@tanstack/react-query';

export const CompleteAccountDetailsScreen: React.FC = (): JSX.Element => {
	const { data: user } = useSession();
	const router = useRouter();
	const { toastError, toastSuccess } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const handleConfirmAccountDetails = async (
		data: FieldValues
	): Promise<void> => {
		setIsLoading(true);
		try {
			const newData: UpdateUser = {
				address1: data.address,
				city: data.cityName,
				state: data.stateName,
				firstName: data.firstName,
				lastName: data.lastName,
				receiveUpdates: false,
				zipCode: data.zipCode,
				phoneNumber: formatPhoneRemove(data.phoneNumber),
			};
			await verificationsService.verifyNumber('phone_verification');
			await userService.completeProfile(newData);
			toastSuccess('You have successfully saved your account details.');
			router.push(AppRoutes.LAWYERAPP_AUTH_VERIFY_PHONE);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const { data: userMe } = useQuery({
		queryKey: [GET_USER_ME],
		queryFn: () => userService.getUserMe(),
		retry: (failureCount, error) => {
			// Do not retry if it is a 401 error
			if ((error as any)?.status === 401) return false;
			// Retry other times according to default settings
			return failureCount < 3;
		},
	});

	return (
		<LayoutLogin
			buttonBack={false}
			rol="lawyer"
			classNameContainer="!max-w-full"
		>
			<div className="mt-8">
				<AccountDetails
					user={userMe ? userMe?.data : user?.user.user}
					isLoading={isLoading}
					handleConfirmAccountDetails={handleConfirmAccountDetails}
					isFieldsRequired={false}
				/>
			</div>
		</LayoutLogin>
	);
};
